/* Robust clipboard helper that survives iframe Permissions-Policy restrictions.

   Why this exists:
   - `navigator.clipboard.writeText` is blocked by default inside cross-origin iframes
     (Chrome 117+) unless the parent explicitly allows `clipboard-write` permission.
     The Emergent "App Preview" pane wraps the preview in an iframe and does NOT
     grant that permission — so the modern API throws NotAllowedError.
   - The legacy `document.execCommand('copy')` trick still works because it relies
     on user-gesture selection, not the Permissions API.

   Usage:
     await copyToClipboard("https://example.com/x");
   Returns: { ok: true, method: 'clipboard' | 'execCommand' | 'prompt' }
*/

import { toast } from "sonner";

export async function copyToClipboard(text) {
  // 1. Try the modern API first — works in normal browser tabs and in correctly-permissioned iframes.
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { ok: true, method: "clipboard" };
    } catch (err) {
      // Fall through to the legacy fallback below. We don't toast here so the caller
      // can decide whether the second method is good enough silently.
      console.debug("clipboard API blocked, trying execCommand:", err?.name || err);
    }
  }

  // 2. Legacy execCommand. Works inside iframes because it operates on a user-selected
  // DOM range, not the Permissions API.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    // Position offscreen + readOnly so the page doesn't visibly jump or steal focus.
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) {
      return { ok: true, method: "execCommand" };
    }
  } catch (err) {
    console.debug("execCommand fallback failed:", err);
  }

  // 3. Both API paths failed — fall back to a window.prompt so the user can manually
  // select + copy. Native prompt is universally available and lets the user grab the
  // URL even when both clipboard paths are sandboxed off.
  try {
    window.prompt("Copy the link below (Cmd/Ctrl+C):", text);
    return { ok: true, method: "prompt" };
  } catch (err) {
    return { ok: false, error: err };
  }
}

/* Wrapper that handles the toast UX consistently across the app. */
export async function copyToClipboardWithToast(text, successMessage = "Copied to clipboard") {
  const result = await copyToClipboard(text);
  if (result.ok) {
    if (result.method === "prompt") {
      // The prompt itself was the UX; no need for an additional success toast.
      return result;
    }
    toast.success(successMessage);
  } else {
    toast.error("Couldn't copy — please copy the link manually.");
  }
  return result;
}
